import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import './DetallePedidoMejorado.css';
import { Edit2, MessageSquare } from 'lucide-react';
import { getApiUrl } from '../../../utils/api';
import AutorizacionModal from '../../Auth/AutorizacionModal';
import MoverProductosModal from '../shared/modals/MoverProductosModal';

// Componente para el resumen de promociones
const PromocionesResumen = memo(({ promocionesAplicadas, totalDescuentos }) => {
  if (!promocionesAplicadas || promocionesAplicadas.length === 0) {
    return null;
  }

  // Filtrar solo promociones tipo 1 (descuentos por porcentaje)
  const promocionesTipo1 = promocionesAplicadas.filter(promo =>
    promo.tipo === '1' || promo.tipo === 'porcentaje'
  );

  if (promocionesTipo1.length === 0) {
    return null;
  }

  return (
    <div className="promociones-aplicadas">
      <div className="promociones-titulo">Descuentos aplicados:</div>
      {promocionesTipo1.map((promo, idx) => {
        const descripcionBase = promo.descripcion || promo.nombre || 'Descuento aplicado';
        const descripcionDetallada = `💰 ${promo.porcentajeDescuento || 0}% descuento: ${descripcionBase}`;

        return (
          <div key={idx} className="promocion-item promocion-descuento">
            <span className="promocion-descripcion">{descripcionDetallada}</span>
            <span className="promocion-descuento">-${promo.descuento.toFixed(2)}</span>
          </div>
        );
      })}

      <div className="total-item descuentos">
        <span>Total Descuentos:</span>
        <span>-${totalDescuentos.toFixed(2)}</span>
      </div>
    </div>
  );
});

// Componente para un solo item del pedido (memoizado para evitar renderizados innecesarios)
const ProductoItem = memo(({
  item,
  esGuardado,
  tieneDuplicados,
  totalCantidad,
  cantidadOtros,
  enPromocion,
  tipoPromocion,
  esDescuento,
  precioOriginal,
  cantidadConDescuento,
  porcentajeDescuento = 0,
  onCambiarCantidad,
  onEliminarProducto,
  onEliminarGuardado,
  onAbrirModalInstrucciones
}) => {
  // Determinar tipos de promoción
  const esPromocionTipo1 = enPromocion && tipoPromocion === '1';
  const esPromocion2x1 = enPromocion && tipoPromocion === '2';
  const esPromocionTipo3 = enPromocion && tipoPromocion === '3';

  // Para promociones, mostrar un indicador visual
  const mostrarBadgePromocion = esPromocionTipo1 || esPromocion2x1 || esPromocionTipo3;

  // Si es una promoción 2x1 y el precio es 0 o tiene descuento, mostrar el precio original tachado
  const mostrarPrecioTachado = (esPromocion2x1 || esPromocionTipo3) && esDescuento && precioOriginal > 0;

  // Obtener la cantidad que tiene descuento (para promociones 2x1 múltiples)
  const cantidadConDescuentoMostrar = cantidadConDescuento > 0 ? cantidadConDescuento : (esDescuento ? item.cantidad : 0);

  // En promociones tipo 2 y 3, NO MOSTRAR agrupación para productos en promoción
  const mostrarAgrupacion = tieneDuplicados && !esPromocionTipo3 && !esPromocion2x1;

  return (
    <div className={`pedido-item ${esGuardado ? 'guardado' : 'nuevo'} ${enPromocion ? 'con-promocion' : ''}`}>
      <div className="item-detalles">
        <div className="item-nombre">
          {esGuardado && <span className="badge impreso">Impreso</span>}
          {mostrarBadgePromocion && (
            <span className="badge promocion-badge">
              {esPromocionTipo1 ?
                (esGuardado ? `${porcentajeDescuento || 0}% aplicado` : `${porcentajeDescuento || 0}% desc`) :
                esDescuento && cantidadConDescuentoMostrar > 0 ?
                  (esPromocionTipo3 ? 'gratis' : `(${cantidadConDescuentoMostrar} promo)`) :
                  'promo'
              }
            </span>
          )}
          {item.nombre}

          {/* Mostrar total solo si hay duplicados y no es promo tipo 3 */}
          {mostrarAgrupacion && (
            <span className="badge total-3">
              TOTAL: {totalCantidad}
            </span>
          )}

          {/* Si es guardado, mostrar cuántos nuevos hay (solo si no es promo tipo 3) */}
          {esGuardado && cantidadOtros > 0 && !esPromocionTipo3 && !esPromocion2x1 && (
            <span className="badge plus-nuevos">
              +{cantidadOtros} NUEVOS
            </span>
          )}

          {/* Si es nuevo, mostrar cuántos guardados hay (solo si no es promo tipo 3) */}
          {!esGuardado && cantidadOtros > 0 && !esPromocionTipo3 && !esPromocion2x1 && (
            <span className="badge plus-guardados">
              +{cantidadOtros} GUARDADOS
            </span>
          )}
        </div>
        <div className="item-precio">
          {mostrarPrecioTachado ? (
            <>
              <span className="precio-tachado">${precioOriginal.toFixed(2)}</span>
              <span className="precio-gratis">$0.00</span>
            </>
          ) : (
            `$${item.precio.toFixed(2)}`
          )}
        </div>
      </div>

      <div className="item-acciones">
        <div className="cantidad-display">
          <span className="cantidad">{item.cantidad}</span>
        </div>

        <div className="item-subtotal">
          {(esPromocionTipo3 || esPromocion2x1) && esDescuento ?
            "$0.00" :
            `$${item.subtotal.toFixed(2)}`
          }
        </div>

        <button
          className="btn-instrucciones"
          onClick={(e) => {
            e.stopPropagation();
            if (onAbrirModalInstrucciones) {
              onAbrirModalInstrucciones(item);
            }
          }}
          title="Agregar instrucciones especiales"
          disabled={esGuardado}
        >
          <MessageSquare size={16} />
        </button>

        <button
          className={`btn-eliminar ${esGuardado ? 'btn-eliminar-guardado' : ''}`}
          onClick={(e) => {
            e.stopPropagation(); // Evitar propagación del evento
            if (esGuardado) {
              onEliminarGuardado(item);
            } else {
              onEliminarProducto(item.idPedidoDetalle);
            }
          }}
        >
          {esGuardado ? '🔒' : '×'}
        </button>
      </div>

      {/* Observaciones si existen */}
      {item.observaciones && item.observaciones.length > 0 && (
        <div className="item-observaciones">
          {item.observaciones.map((obs, idx) => (
            <div key={idx} className="observacion-tag">
              {obs}
            </div>
          ))}
        </div>
      )}

      {/* Instrucciones especiales si existen */}
      {item.instrucciones && (
        <div className="item-instrucciones">
          <span className="instrucciones-label">📝 Instrucciones:</span>
          <span className="instrucciones-texto">{item.instrucciones}</span>
        </div>
      )}

      {/* Información adicional de la promoción */}
      {esPromocionTipo1 && (
        <div className="promocion-info">
          <span className="promocion-detalle">
            {esGuardado ?
              `Descuento del ${porcentajeDescuento}% ya aplicado en precio` :
              `Descuento del ${porcentajeDescuento}% aplicado`
            }
          </span>
        </div>
      )}

      {esPromocion2x1 && (
        <div className="promocion-info">
          {esDescuento ? (
            cantidadConDescuentoMostrar > 1 ? (
              <span className="promocion-detalle">{cantidadConDescuentoMostrar} unidades con descuento (2x1)</span>
            ) : (
              <span className="promocion-detalle">Producto con descuento (2x1)</span>
            )
          ) : (
            <span className="promocion-detalle">Aplica promoción 2x1</span>
          )}
        </div>
      )}

      {/* Información para promociones tipo 3 */}
      {esPromocionTipo3 && (
        <div className="promocion-info">
          {esDescuento ? (
            <span className="promocion-detalle">Producto gratis (combo especial)</span>
          ) : (
            <span className="promocion-detalle">Parte de combo especial</span>
          )}
        </div>
      )}
    </div>
  );
});

// Componente para la lista de productos (sección de nuevos o guardados)
const ListaProductos = memo(({
  productos,
  esGuardados,
  productosComplementarios,
  infoPromociones,
  onCambiarCantidad,
  onEliminarProducto,
  onEliminarGuardado,
  onAbrirModalInstrucciones
}) => {
  // Función para obtener información de totales
  const obtenerInfoProducto = useCallback((producto) => {
    // Función para verificar si dos productos son el mismo tipo
    const esMismoProducto = (p1, p2) => {
      return (
        (p1.idProducto === p2.idProducto ||
          p1.idPlatillo === p2.idPlatillo) &&
        p1.nombre === p2.nombre
      );
    };

    // Verificar si este producto está en promociones
    const infoPromocion = infoPromociones.get(producto.idPedidoDetalle) || {};
    const esPromocionTipo1 = infoPromocion.enPromocion && infoPromocion.tipoPromocion === '1';
    const esPromocionTipo2 = infoPromocion.enPromocion && infoPromocion.tipoPromocion === '2';
    const esPromocionTipo3 = infoPromocion.enPromocion && infoPromocion.tipoPromocion === '3';

    // Para promociones tipo 1 (descuentos por porcentaje), el descuento ya está aplicado
    // Solo marcamos el producto como en promoción para mostrar indicadores visuales
    if (esPromocionTipo1) {
      // Buscar productos complementarios normalmente para tipo 1
      const productosComplementariosDelMismoTipo = productosComplementarios.filter(p =>
        esMismoProducto(p, producto)
      );

      const cantidadComplementarios = productosComplementariosDelMismoTipo.reduce(
        (total, p) => total + p.cantidad,
        0
      );

      const cantidadTotal = producto.cantidad + cantidadComplementarios;
      const tieneDuplicados = cantidadComplementarios > 0;

      return {
        tieneDuplicados,
        cantidadTotal,
        cantidadComplementarios,
        enPromocion: true,
        tipoPromocion: '1',
        esDescuento: true, // Indica que tiene descuento (ya aplicado)
        cantidadConDescuento: 0,
        precioOriginal: null, // No mostramos precio tachado para tipo 1
        porcentajeDescuento: infoPromocion.porcentajeDescuento || 0,
        yaAplicado: infoPromocion.yaAplicado || false
      };
    }

    // Para promociones tipo 2 y 3, NO BUSCAMOS productos complementarios
    // Esto asegura que cada producto se muestre como un ítem independiente
    if (esPromocionTipo3 || (esPromocionTipo2 && infoPromocion.esDescuento)) {
      const cantidadEnPromo = infoPromocion.cantidad || producto.cantidad;
      const cantidadExtra = producto.cantidad - cantidadEnPromo;
      if (cantidadExtra > 0 && infoPromocion.esDescuento) {
        // Ajustar la cantidad en el componente para mostrar solo la cantidad promocional
        return {
          tieneDuplicados: false,
          tieneExtra: true, // Indicador de que hay unidades extras
          cantidadEnPromo: cantidadEnPromo, // Cantidad con descuento
          cantidadExtra: cantidadExtra, // Cantidad sin descuento
          cantidadTotal: producto.cantidad,
          cantidadComplementarios: 0,
          enPromocion: infoPromocion.enPromocion || false,
          tipoPromocion: infoPromocion.tipoPromocion || null,
          esDescuento: infoPromocion.esDescuento || false,
          cantidadConDescuento: infoPromocion.cantidad || 0,
          precioOriginal: infoPromocion.precioOriginal || producto.precio,
          porcentajeDescuento: infoPromocion.porcentajeDescuento || 0
        };
      }
      return {
        tieneDuplicados: false,
        cantidadTotal: producto.cantidad,
        cantidadComplementarios: 0,
        enPromocion: infoPromocion.enPromocion || false,
        tipoPromocion: infoPromocion.tipoPromocion || null,
        esDescuento: infoPromocion.esDescuento || false,
        cantidadConDescuento: infoPromocion.cantidad || 0,
        precioOriginal: infoPromocion.precioOriginal || producto.precio,
        porcentajeDescuento: infoPromocion.porcentajeDescuento || 0
      };
    }

    // Para productos que se cobran normal en promociones tipo 2
    if (esPromocionTipo2 && !infoPromocion.esDescuento) {
      return {
        tieneDuplicados: false,
        cantidadTotal: producto.cantidad,
        cantidadComplementarios: 0,
        enPromocion: infoPromocion.enPromocion || false,
        tipoPromocion: infoPromocion.tipoPromocion || null,
        esDescuento: infoPromocion.esDescuento || false,
        cantidadConDescuento: 0,
        precioOriginal: null,
        porcentajeDescuento: infoPromocion.porcentajeDescuento || 0
      };
    }

    // Para otros tipos de promociones, mantener el comportamiento de buscar productos complementarios
    const productosComplementariosDelMismoTipo = productosComplementarios.filter(p =>
      esMismoProducto(p, producto)
    );

    // Calcular totales
    const cantidadComplementarios = productosComplementariosDelMismoTipo.reduce(
      (total, p) => total + p.cantidad,
      0
    );

    const cantidadTotal = producto.cantidad + cantidadComplementarios;
    const tieneDuplicados = cantidadComplementarios > 0;

    return {
      tieneDuplicados,
      cantidadTotal,
      cantidadComplementarios,
      enPromocion: infoPromocion.enPromocion || false,
      tipoPromocion: infoPromocion.tipoPromocion || null,
      esDescuento: infoPromocion.esDescuento || false,
      cantidadConDescuento: infoPromocion.cantidad || 0,
      precioOriginal: infoPromocion.precioOriginal || producto.precio,
      porcentajeDescuento: infoPromocion.porcentajeDescuento || 0
    };
  }, [productosComplementarios, infoPromociones]);

  if (productos.length === 0) {
    return (
      <div className="pedido-vacio">
        {esGuardados ? "No hay productos guardados" : "No hay productos nuevos"}
      </div>
    );
  }

  return (
    <div className="pedido-scroll">
      {productos.map(item => {
        const {
          tieneDuplicados,
          tieneExtra,
          cantidadEnPromo,
          cantidadExtra,
          cantidadTotal,
          cantidadComplementarios,
          enPromocion,
          tipoPromocion,
          esDescuento,
          cantidadConDescuento,
          precioOriginal,
          porcentajeDescuento
        } = obtenerInfoProducto(item);

        // Si el producto tiene unidades en promo y extra, generar dos componentes
        if (tieneExtra && esDescuento && enPromocion && (tipoPromocion === '3' || tipoPromocion === '2')) {
          // Crear componente para las unidades en promoción (precio $0)
          const itemPromo = {
            ...item,
            cantidad: cantidadEnPromo,
            subtotal: 0
          };

          // Crear componente para las unidades fuera de promoción (precio normal)
          const itemExtra = {
            ...item,
            cantidad: cantidadExtra,
            subtotal: cantidadExtra * item.precio
          };

          return (
            <React.Fragment key={item.idPedidoDetalle}>
              {/* Renderizar unidades en promoción (gratis) */}
              <ProductoItem
                key={`${item.idPedidoDetalle}_promo`}
                item={itemPromo}
                esGuardado={esGuardados}
                tieneDuplicados={false}
                totalCantidad={cantidadEnPromo}
                cantidadOtros={0}
                enPromocion={true}
                tipoPromocion={tipoPromocion}
                esDescuento={true}
                cantidadConDescuento={cantidadEnPromo}
                precioOriginal={precioOriginal}
                porcentajeDescuento={porcentajeDescuento}
                onCambiarCantidad={onCambiarCantidad}
                onEliminarProducto={onEliminarProducto}
                onEliminarGuardado={onEliminarGuardado}
                onAbrirModalInstrucciones={onAbrirModalInstrucciones}
              />

              {/* Renderizar unidades extra (precio normal) */}
              <ProductoItem
                key={`${item.idPedidoDetalle}_extra`}
                item={itemExtra}
                esGuardado={esGuardados}
                tieneDuplicados={false}
                totalCantidad={cantidadExtra}
                cantidadOtros={0}
                enPromocion={false}
                tipoPromocion={null}
                esDescuento={false}
                cantidadConDescuento={0}
                precioOriginal={null}
                porcentajeDescuento={0}
                onCambiarCantidad={onCambiarCantidad}
                onEliminarProducto={onEliminarProducto}
                onEliminarGuardado={onEliminarGuardado}
                onAbrirModalInstrucciones={onAbrirModalInstrucciones}
              />
            </React.Fragment>
          );
        }

        // Para los demás casos, sigue el comportamiento normal
        return (
          <ProductoItem
            key={item.idPedidoDetalle}
            item={item}
            esGuardado={esGuardados}
            tieneDuplicados={tieneDuplicados}
            totalCantidad={cantidadTotal}
            cantidadOtros={cantidadComplementarios}
            enPromocion={enPromocion}
            tipoPromocion={tipoPromocion}
            esDescuento={esDescuento}
            cantidadConDescuento={cantidadConDescuento}
            precioOriginal={precioOriginal}
            porcentajeDescuento={porcentajeDescuento}
            onCambiarCantidad={onCambiarCantidad}
            onEliminarProducto={onEliminarProducto}
            onEliminarGuardado={onEliminarGuardado}
            onAbrirModalInstrucciones={onAbrirModalInstrucciones}
          />
        );
      })}
    </div>
  );
});

// Componente principal de detalle de pedido
const DetallePedidoMejorado = ({
  pedidoActual = [],
  productosNuevos = [],
  productosGuardados = [],
  mesaActual = null, // Información de la mesa actual para mover productos
  onCambiarCantidad,
  onEliminarProducto,
  onEliminarProductoGuardado,
  onLimpiarPedido,
  onImprimirComanda,
  onEditarInstrucciones = null, // Valor por defecto
  onRecargarPedido = null, // Callback para recargar datos del pedido sin reload
  promocionesAplicadas = [],
  calculandoPromociones = false,
  // Nuevas props para promociones de mesa en pedidos para llevar
  tipoPedido = 'mesa', // 'mesa', 'llevar', 'domicilio', etc.
  promocionMesaActiva = false, // Estado del toggle de promoción de mesa
  onTogglePromocionMesa = null // Callback para activar/desactivar promociones de mesa
}) => {
  // Estados para el modal de autorización
  const [mostrarModalAutorizacion, setMostrarModalAutorizacion] = useState(false);
  const [productoAEliminar, setProductoAEliminar] = useState(null);

  // Estados para el modal de mover productos
  const [mostrarModalMover, setMostrarModalMover] = useState(false);

  // Estados para el modal de instrucciones
  const [mostrarModalInstrucciones, setMostrarModalInstrucciones] = useState(false);
  const [productoEditando, setProductoEditando] = useState(null);
  const [instruccionesTemp, setInstruccionesTemp] = useState('');

  // Estados para la visualización
  const [mostrarGuardados, setMostrarGuardados] = useState(true);
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);

  // Función para obtener información de las promociones
  const obtenerInfoPromociones = useCallback(() => {
    // Mapeo para los productos en promociones
    const productosEnPromocion = new Map();

    // Procesar promociones tipo 1 (descuentos por porcentaje)
    const promociones1 = promocionesAplicadas.filter(p => p.tipo === '1' || p.tipo === 'porcentaje');
    promociones1.forEach(promo => {
      // Para promociones tipo 1, distinguir entre productos nuevos y guardados
      if (promo.productosIncluidos) {
        promo.productosIncluidos.forEach(idPedidoDetalle => {
          // Verificar si el producto está en la lista de productos guardados
          const esProductoGuardado = productosGuardados.some(p => p.idPedidoDetalle === idPedidoDetalle);
          const esProductoNuevo = productosNuevos.some(p => p.idPedidoDetalle === idPedidoDetalle);

          // Solo aplicar descuento visual a productos nuevos
          // Los productos guardados ya tienen el descuento aplicado en su precio
          if (esProductoNuevo) {
            productosEnPromocion.set(idPedidoDetalle, {
              enPromocion: true,
              tipoPromocion: '1',
              esDescuento: true, // El descuento se mostrará en el badge
              porcentajeDescuento: promo.porcentajeDescuento || 0,
              promocionId: promo.id || promo.idPromocion,
              precioOriginal: null, // No mostrar precio tachado
              yaAplicado: false // Descuento aún no aplicado físicamente
            });
          } else if (esProductoGuardado) {
            // Para productos guardados, marcar como ya aplicado sin descuento visual adicional
            productosEnPromocion.set(idPedidoDetalle, {
              enPromocion: true,
              tipoPromocion: '1',
              esDescuento: false, // NO aplicar descuento adicional
              porcentajeDescuento: promo.porcentajeDescuento || 0,
              promocionId: promo.id || promo.idPromocion,
              precioOriginal: null,
              yaAplicado: true // Descuento ya aplicado físicamente al precio guardado
            });
          }
        });
      }
    });

    // Procesar promociones 2x1
    const promociones2x1 = promocionesAplicadas.filter(p => p.tipo === '2' || p.tipo === '2x1');
    promociones2x1.forEach(promo => {
      // Verificar si tenemos información detallada de cantidades
      if (promo.productosIncluidosDetalle && promo.productosGratisDetalle) {
        // Procesar productos incluidos (precio normal)
        promo.productosIncluidosDetalle.forEach(info => {
          productosEnPromocion.set(info.idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '2',
            esDescuento: false,
            cantidad: info.cantidad,
            precioOriginal: null, // No hay descuento
            promocionId: promo.id || promo.idPromocion,
            // Añadir referencia al conjunto de la promoción para agrupar por promoción
            conjuntoPromoId: `promo2_${promo.id || promo.idPromocion}`
          });
        });

        // Procesar productos con descuento
        promo.productosGratisDetalle.forEach(info => {
          const producto = pedidoActual.find(p => p.idPedidoDetalle === info.idPedidoDetalle);

          // IMPORTANTE: Guardar la cantidad exacta que entra en la promoción
          const cantidadEnPromo = info.cantidad || 1;
          const cantidadTotal = producto ? producto.cantidad : cantidadEnPromo;

          productosEnPromocion.set(info.idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '2',
            esDescuento: true,
            cantidad: cantidadEnPromo, // Cantidad que entra en promoción
            cantidadTotal: cantidadTotal, // Cantidad total del producto
            precioOriginal: producto ? producto.precio : 0,
            promocionId: promo.id || promo.idPromocion,
            // Añadir referencia al conjunto de la promoción para agrupar
            conjuntoPromoId: `promo2_${promo.id || promo.idPromocion}`
          });
        });
      }
      // Compatibilidad con el formato anterior
      else if (promo.productosIncluidos && promo.productosGratis) {
        // Procesar productos incluidos (precio normal)
        promo.productosIncluidos.forEach(idPedidoDetalle => {
          productosEnPromocion.set(idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '2',
            esDescuento: false,
            precioOriginal: null,
            promocionId: promo.id || promo.idPromocion,
            // Añadir referencia al conjunto de la promoción
            conjuntoPromoId: `promo2_${promo.id || promo.idPromocion}`
          });
        });

        // Procesar productos con descuento
        promo.productosGratis.forEach(idPedidoDetalle => {
          const producto = pedidoActual.find(p => p.idPedidoDetalle === idPedidoDetalle);

          productosEnPromocion.set(idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '2',
            esDescuento: true,
            precioOriginal: producto ? producto.precio : 0,
            promocionId: promo.id || promo.idPromocion,
            // Añadir referencia al conjunto de la promoción
            conjuntoPromoId: `promo2_${promo.id || promo.idPromocion}`
          });
        });
      }
    });

    // Procesar promociones tipo 3
    const promociones3 = promocionesAplicadas.filter(p => p.tipo === '3' || p.tipo === 'XporY');
    promociones3.forEach(promo => {
      // Verificar si tenemos información detallada
      if (promo.productosIncluidosDetalle && promo.productosGratisDetalle) {
        // Procesar productos incluidos (precio normal)
        promo.productosIncluidosDetalle.forEach(info => {
          productosEnPromocion.set(info.idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '3',
            esDescuento: false,
            cantidad: info.cantidad || 1, // Cantidad exacta en la promoción
            cantidadTotal: info.cantidad || 1, // Total de unidades de este producto
            precioOriginal: null, // No hay descuento
            promocionId: promo.id || promo.idPromocion,
            conjuntoPromoId: `promo3_${promo.id || promo.idPromocion}`
          });
        });

        // Procesar productos con descuento (gratis)
        promo.productosGratisDetalle.forEach(info => {
          const producto = pedidoActual.find(p => p.idPedidoDetalle === info.idPedidoDetalle);

          // IMPORTANTE: Guardar la cantidad exacta que entra en la promoción
          const cantidadEnPromo = info.cantidad || 1;
          const cantidadTotal = producto ? producto.cantidad : cantidadEnPromo;

          productosEnPromocion.set(info.idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '3',
            esDescuento: true,
            cantidad: cantidadEnPromo, // Cantidad que entra en promoción
            cantidadTotal: cantidadTotal, // Cantidad total del producto
            precioOriginal: producto ? producto.precio : 0,
            promocionId: promo.id || promo.idPromocion,
            conjuntoPromoId: `promo3_${promo.id || promo.idPromocion}`
          });
        });
      }
      // Compatibilidad con el formato anterior
      else if (promo.productosIncluidos && promo.productosGratis) {
        // Procesar productos incluidos (precio normal)
        promo.productosIncluidos.forEach(idPedidoDetalle => {
          productosEnPromocion.set(idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '3',
            esDescuento: false,
            precioOriginal: null,
            promocionId: promo.id || promo.idPromocion,
            // Añadir referencia al conjunto de la promoción
            conjuntoPromoId: `promo3_${promo.id || promo.idPromocion}`
          });
        });

        // Procesar productos con descuento
        promo.productosGratis.forEach(idPedidoDetalle => {
          const producto = pedidoActual.find(p => p.idPedidoDetalle === idPedidoDetalle);

          productosEnPromocion.set(idPedidoDetalle, {
            enPromocion: true,
            tipoPromocion: '3',
            esDescuento: true,
            precioOriginal: producto ? producto.precio : 0,
            promocionId: promo.id || promo.idPromocion,
            // Añadir referencia al conjunto de la promoción
            conjuntoPromoId: `promo3_${promo.id || promo.idPromocion}`
          });
        });
      }
    });

    return productosEnPromocion;
  }, [promocionesAplicadas, pedidoActual, productosGuardados, productosNuevos]);

  // Memoizar la información de promociones
  const infoPromociones = useMemo(() => {
    return obtenerInfoPromociones();
  }, [obtenerInfoPromociones]);

  // Memoizar el resumen consolidado
  const resumenConsolidado = useMemo(() => {
    const productosMap = new Map();        // Para productos normales (sin promoción)
    const productosPromo3 = new Map();     // Para productos específicos de promoción tipo 3
    const productosNoPromo = new Map();    // Para productos del mismo tipo que no entran en promoción

    // Función para obtener una clave única para cada producto
    const getProductKey = (producto) => {
      return `${producto.idProducto || producto.idPlatillo}_${producto.nombre}_${producto.precio}`;
    };

    // PASO 1: Identificar productos en promociones tipo 2 y 3
    // Esto nos ayudará a determinar qué productos no son parte de promociones
    const productosEnPromoTipo3 = new Set();
    const productosEnPromoTipo2 = new Set();
    pedidoActual.forEach(producto => {
      const infoPromo = infoPromociones.get(producto.idPedidoDetalle);
      if (infoPromo && infoPromo.tipoPromocion === '3') {
        productosEnPromoTipo3.add(producto.idPedidoDetalle);
      }
      if (infoPromo && infoPromo.tipoPromocion === '2') {
        productosEnPromoTipo2.add(producto.idPedidoDetalle);
      }
    });

    // PASO 2: Procesar todos los productos (tanto nuevos como guardados)
    [...productosGuardados, ...productosNuevos].forEach(producto => {
      const key = getProductKey(producto);
      const infoPromo = infoPromociones.get(producto.idPedidoDetalle);
      const esPromocionTipo3 = infoPromo && infoPromo.tipoPromocion === '3';
      const esPromocionTipo2 = infoPromo && infoPromo.tipoPromocion === '2';

      // CASO 1: Producto en promoción tipo 3
      if (esPromocionTipo3) {
        // Crear una clave única que incluye el ID del detalle para evitar agrupación
        // y añadir información sobre si es gratis o no
        const keyUnica = `${infoPromo.conjuntoPromoId || 'promo3'}_${infoPromo.esDescuento ? 'gratis' : 'cobrar'}_${producto.idPedidoDetalle}`;

        productosPromo3.set(keyUnica, {
          ...producto,
          esGuardado: productosGuardados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle),
          esPromocionTipo3: true,
          esPromocionTipo2: false,
          esDescuento: infoPromo.esDescuento,
          // Si es producto gratis, ajustar precio y subtotal a 0
          precio: infoPromo.esDescuento ? 0 : producto.precio,
          subtotal: infoPromo.esDescuento ? 0 : producto.subtotal,
          precioOriginal: infoPromo.esDescuento ? producto.precio : null
        });
      }
      // CASO 1.5: Producto en promoción tipo 2 (similar a tipo 3)
      else if (esPromocionTipo2 && infoPromo.esDescuento) {
        // Crear una clave única para productos con descuento en promo tipo 2
        // Agruparemos solo los productos con descuento que sean del mismo tipo
        // Los productos que se cobran normal no se agrupan
        const keyUnica = `${infoPromo.conjuntoPromoId || 'promo2'}_${infoPromo.esDescuento ? 'gratis' : 'cobrar'}_${producto.idProducto || producto.idPlatillo}`;

        productosPromo3.set(keyUnica, {
          ...producto,
          esGuardado: productosGuardados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle),
          esPromocionTipo3: false,
          esPromocionTipo2: true,
          esDescuento: infoPromo.esDescuento,
          // Si es producto gratis, ajustar precio y subtotal a 0
          precio: infoPromo.esDescuento ? 0 : producto.precio,
          subtotal: infoPromo.esDescuento ? 0 : producto.subtotal,
          precioOriginal: infoPromo.esDescuento ? producto.precio : null
        });
      }
      // CASO 1.6: Producto en promoción tipo 2 que se cobra normal
      else if (esPromocionTipo2 && !infoPromo.esDescuento) {
        // Los productos que se cobran normal en promoción tipo 2 van separados
        const keyUnica = `promo2_normal_${producto.idPedidoDetalle}`;
        productosNoPromo.set(keyUnica, {
          ...producto,
          esGuardado: productosGuardados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle),
          esPromocionTipo3: false,
          esPromocionTipo2: true,
          esDescuento: false
        });
      }
      // CASO 2: Producto que podría estar en promo tipo 2 o 3 pero no lo está
      // (del mismo tipo pero fuera de la promoción)
      else if (!esPromocionTipo3 && !esPromocionTipo2 &&
        (!productosEnPromoTipo3.has(producto.idPedidoDetalle) &&
          !productosEnPromoTipo2.has(producto.idPedidoDetalle))) {

        // Verificar si es del mismo tipo que algún producto en promo tipo 2 o 3
        const idProducto = producto.idProducto || producto.idPlatillo;

        // Si es un producto del mismo tipo que alguno en promoción, manejarlo separado
        const esTipoEnPromo = [...productosEnPromoTipo3, ...productosEnPromoTipo2].some(idDetalle => {
          const productoEnPromo = pedidoActual.find(p => p.idPedidoDetalle === idDetalle);
          return productoEnPromo &&
            (productoEnPromo.idProducto === idProducto ||
              productoEnPromo.idPlatillo === idProducto);
        });

        if (esTipoEnPromo) {
          // Crear key única para evitar agrupación
          const keyUnica = `nopromo_${key}_${producto.idPedidoDetalle}`;
          productosNoPromo.set(keyUnica, {
            ...producto,
            esGuardado: productosGuardados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle),
            esPromocionTipo3: false,
            esPromocionTipo2: false
          });
        }
        // CASO 3: Producto normal (sin relación con promo tipo 2 o 3)
        else {
          if (productosMap.has(key)) {
            // Si ya existe, incrementar cantidad y subtotal
            const existente = productosMap.get(key);
            existente.cantidad += producto.cantidad;
            existente.subtotal += producto.subtotal;

            // Marcar si tiene tanto guardados como nuevos
            if (productosGuardados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle) &&
              productosNuevos.some(p => p.idPedidoDetalle === producto.idPedidoDetalle)) {
              existente.tieneMixto = true;
            }
          } else {
            // Si no existe, agregar al mapa
            productosMap.set(key, {
              ...producto,
              tieneMixto: false,
              esGuardado: productosGuardados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle)
            });
          }
        }
      }
    });

    // Convertir todos los mapas a arrays y combinarlos
    const productosAgrupados = Array.from(productosMap.values());
    const productosPromo3List = Array.from(productosPromo3.values());
    const productosNoPromoList = Array.from(productosNoPromo.values());

    // Combinar todos los arrays
    return [...productosAgrupados, ...productosPromo3List, ...productosNoPromoList];
  }, [pedidoActual, productosGuardados, productosNuevos, infoPromociones]);

  // Memoizar los cálculos de totales
  const subtotal = useMemo(() => {
    return pedidoActual.reduce((total, item) => total + item.subtotal, 0);
  }, [pedidoActual]);

  const totalDescuentos = useMemo(() => {
    // Debug: Mostrar información de productos
    console.log("🔍 DEBUG - Productos Guardados:", productosGuardados.map(p => ({ id: p.idPedidoDetalle, nombre: p.nombre })));
    console.log("🔍 DEBUG - Promociones Aplicadas COMPLETAS:", promocionesAplicadas);
    console.log("🔍 DEBUG - Promociones Aplicadas RESUMIDAS:", promocionesAplicadas.map(p => ({
      tipo: p.tipo,
      productos: p.productosIncluidos,
      descuento: p.descuento,
      idPromocion: p.idPromocion,
      nombre: p.nombre
    })));

    // Filtrar promociones para evitar descuentos duplicados en productos guardados
    const promocionesFiltradas = promocionesAplicadas.filter(promo => {
      // Si no es promoción tipo 1, incluir siempre
      if (promo.tipo !== '1' && promo.tipo !== 'porcentaje') {
        return true;
      }

      console.log(`🔍 DEBUG - Procesando promoción tipo ${promo.tipo}:`, promo);

      // Para promociones tipo 1, verificar si incluye productos guardados
      const productosEnPromocion = promo.productosIncluidos || promo.productos || [];
      if (productosEnPromocion.length > 0) {
        console.log(`🔍 DEBUG - Productos en promoción:`, productosEnPromocion);
        console.log(`🔍 DEBUG - IDs de productos guardados:`, productosGuardados.map(p => p.idPedidoDetalle));

        // Verificar si TODOS los productos de esta promoción están guardados
        const todosLosProductosGuardados = productosEnPromocion.every(idPedidoDetalle => {
          const estaGuardado = productosGuardados.some(p => p.idPedidoDetalle == idPedidoDetalle); // usar == para comparar string vs number
          console.log(`🔍 DEBUG - Producto ${idPedidoDetalle} está guardado:`, estaGuardado);
          return estaGuardado;
        });

        console.log(`🔍 DEBUG - Todos los productos guardados:`, todosLosProductosGuardados);

        // Si todos están guardados, NO incluir esta promoción en el total
        // porque el descuento ya está aplicado físicamente en los precios guardados
        const incluir = !todosLosProductosGuardados;
        console.log(`🔍 DEBUG - ¿Incluir promoción en total?:`, incluir);
        return incluir;
      }

      return true;
    });

    console.log("🔍 DEBUG - Promociones Filtradas:", promocionesFiltradas.map(p => ({ tipo: p.tipo, descuento: p.descuento })));

    return promocionesFiltradas.reduce((total, promo) => total + promo.descuento, 0);
  }, [promocionesAplicadas, productosGuardados]);

  // Memoizar promociones filtradas para mostrar en UI
  const promocionesParaMostrar = useMemo(() => {
    return promocionesAplicadas.filter(promo => {
      // Solo mostrar promociones tipo 1 (descuentos por porcentaje)
      if (promo.tipo !== '1' && promo.tipo !== 'porcentaje') {
        return false;
      }

      // Para promociones tipo 1, verificar si incluye productos guardados
      const productosEnPromocion = promo.productosIncluidos || promo.productos || [];
      if (productosEnPromocion.length > 0) {
        // Verificar si TODOS los productos de esta promoción están guardados
        const todosLosProductosGuardados = productosEnPromocion.every(idPedidoDetalle =>
          productosGuardados.some(p => p.idPedidoDetalle == idPedidoDetalle) // usar == para comparar string vs number
        );

        // Si todos están guardados, NO mostrar esta promoción
        // porque el descuento ya está aplicado físicamente en los precios guardados
        return !todosLosProductosGuardados;
      }

      return true;
    });
  }, [promocionesAplicadas, productosGuardados]);

  const total = useMemo(() => {
    return subtotal - totalDescuentos;
  }, [subtotal, totalDescuentos]);

  // Función para intentar eliminar un producto ya guardado
  const intentarEliminarProductoGuardado = (producto) => {
    setProductoAEliminar(producto);
    setMostrarModalAutorizacion(true);
  };

  // Función para manejar la autorización exitosa
  const handleAutorizacionExitosa = async (authData) => {
    try {
      // Llamar a la función para cancelar el producto guardado con los datos de autorización
      const resultado = await onEliminarProductoGuardado(
        productoAEliminar.idPedidoDetalle,
        authData
      );

      if (resultado.success) {
        setMostrarModalAutorizacion(false);
        setProductoAEliminar(null);
      } else {
        console.error('Error al eliminar producto:', resultado.message);
        // El error se mostrará en el modal si es necesario
      }
    } catch (error) {
      console.error('Error al eliminar producto guardado:', error);
    }
  };

  // Función para manejar cancelación de autorización
  const handleAutorizacionCancelada = () => {
    setMostrarModalAutorizacion(false);
    setProductoAEliminar(null);
  };

  // Función para manejar mover productos entre mesas
  const handleMoverProductos = async ({ productos, mesaDestino, mesaOrigen, authData }) => {
    try {
      const payload = {
        tipo: 'moverProductosEntreMesas',
        productosIds: productos.map(p => p.idPedidoDetalle),
        idMesaDestino: mesaDestino.idMesa,
        idPedidoDestino: mesaDestino.idPedido,
        usuarioAutorizado: authData.usuario,
        razonMovimiento: authData.razon || null,
        metodoAuth: authData.metodoAuth
      };

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!data.success) {
        alert(`Error al mover productos: ${data.message}`);
        return { success: false, message: data.message };
      }

      // NO llamar a onEliminarProductoGuardado porque eso inserta en cancelados
      // Los productos ya fueron movidos correctamente en el backend

      alert(`${productos.length} producto(s) movidos exitosamente a ${mesaDestino.nombreMesa || `Mesa ${mesaDestino.numero}`}`);

      // Recargar datos del pedido sin refrescar la página
      if (onRecargarPedido) {
        await onRecargarPedido();
      }

      return { success: true, data: data.data };
    } catch (error) {
      console.error('Error al mover productos:', error);
      alert('Error de conexión al mover productos');
      return { success: false, message: 'Error de conexión' };
    }
  };

  // Función para abrir el modal de instrucciones
  const abrirModalInstrucciones = useCallback((producto) => {
    // Si no se proporciona la función, mostrar advertencia pero permitir abrir el modal
    if (!onEditarInstrucciones) {
      console.warn('onEditarInstrucciones no está definido. Las instrucciones no se guardarán.');
    }
    setProductoEditando(producto);
    setInstruccionesTemp(producto.instrucciones || '');
    setMostrarModalInstrucciones(true);
  }, [onEditarInstrucciones]);

  // Función para guardar las instrucciones
  const guardarInstrucciones = useCallback(() => {
    if (productoEditando) {
      if (onEditarInstrucciones) {
        onEditarInstrucciones(productoEditando.idPedidoDetalle, instruccionesTemp);
      } else {
        console.warn('No se puede guardar las instrucciones: onEditarInstrucciones no está definido');
        // Aún así cerrar el modal para mejor UX
      }
    }
    setMostrarModalInstrucciones(false);
    setProductoEditando(null);
    setInstruccionesTemp('');
  }, [productoEditando, instruccionesTemp, onEditarInstrucciones]);

  // Modal de autorización usando AutorizacionModal
  const modalAutorizacion = useMemo(() => {
    return (
      <AutorizacionModal
        isOpen={mostrarModalAutorizacion}
        onClose={handleAutorizacionCancelada}
        onAuthorize={handleAutorizacionExitosa}
        accion={`eliminar producto guardado "${productoAEliminar?.nombre}"`}
        perfilRequerido="cancelarPedidosProductos"
        titulo="Autorización para Cancelación"
        descripcion="Se requiere autorización para eliminar un producto que ya ha sido guardado/impreso"
        solicitarRazon={true}
        labelRazon="Motivo de la cancelación"
      />
    );
  }, [mostrarModalAutorizacion, productoAEliminar, handleAutorizacionExitosa, handleAutorizacionCancelada]);

  // Modal de instrucciones
  const modalInstrucciones = useMemo(() => {
    if (!mostrarModalInstrucciones) return null;

    return (
      <div className="modal-overlay modal-overlay-instrucciones">
        <div className="modal-instrucciones">
          <div className="modal-header">
            <h3>Instrucciones Especiales</h3>
            <button
              className="btn-cerrar-modal"
              onClick={() => setMostrarModalInstrucciones(false)}
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            <p className="producto-nombre">
              <strong>{productoEditando?.nombre}</strong>
            </p>
            <div className="instrucciones-input-container">
              <label htmlFor="instrucciones">
                Agregue instrucciones especiales para este producto:
              </label>
              <textarea
                id="instrucciones"
                className="instrucciones-textarea"
                value={instruccionesTemp}
                onChange={(e) => setInstruccionesTemp(e.target.value)}
                placeholder="Ej: Sin cebolla, sin picante, bien cocido, etc."
                rows="4"
                maxLength="200"
              />
              <div className="caracteres-contador">
                {instruccionesTemp.length}/200 caracteres
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn-confirmar"
              onClick={guardarInstrucciones}
            >
              Guardar
            </button>
            <button
              className="btn-cancelar"
              onClick={() => setMostrarModalInstrucciones(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }, [mostrarModalInstrucciones, productoEditando, instruccionesTemp, guardarInstrucciones]);

  return (
    <div className={`pedido-seccion ${pantallaCompleta ? 'pantalla-completa' : ''}`}>
      {/* Header del pedido */}
      <div className="pedido-header">
        <div className="pedido-titulo">
          <h2>Detalle del Pedido</h2>
          <div className="pedido-controles">
            <button
              className="btn-vista-toggle"
              title="Cambiar vista"
              onClick={() => setMostrarResumen(!mostrarResumen)}
            >
              {mostrarResumen ? '🔍 Detalle' : '📋 Resumen'}
            </button>
            <button
              className="btn-pantalla-completa"
              title="Pantalla completa"
              onClick={() => setPantallaCompleta(!pantallaCompleta)}
            >
              {pantallaCompleta ? '↙️' : '↗️'}
            </button>
          </div>
        </div>
        <div className="pedido-acciones">
          {productosNuevos.length > 0 && (
            <button
              className="btn-imprimir"
              onClick={onImprimirComanda}
            >
              🖨️ Imprimir
            </button>
          )}

          {productosGuardados.length > 0 && (
            <button
              className="btn-mover-productos"
              onClick={() => setMostrarModalMover(true)}
              title="Mover productos a otra mesa"
            >
              🔄 Mover Productos
            </button>
          )}

          {/* Botón toggle para aplicar promociones de mesa en pedidos para llevar */}
          {tipoPedido === 'llevar' && onTogglePromocionMesa && (
            <button
              className={`btn-promocion-mesa ${promocionMesaActiva ? 'activo' : ''}`}
              onClick={onTogglePromocionMesa}
              title={promocionMesaActiva ? 'Desactivar promociones de mesa' : 'Aplicar promociones de mesa'}
            >
              {promocionMesaActiva ? '🏷️ Promo Mesa ON' : '🏷️ Promo Mesa'}
            </button>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="pedido-contenido">
        {!mostrarResumen ? (
          // Vista detallada
          <>
            {/* Sección para productos nuevos */}
            <div className="seccion-nuevos">
              <div className="seccion-titulo">
                <h3>Productos Nuevos <span className="contador">{productosNuevos.length}</span></h3>
              </div>
              <div className="pedido-lista nuevos-lista">
                <ListaProductos
                  productos={productosNuevos}
                  esGuardados={false}
                  productosComplementarios={productosGuardados}
                  infoPromociones={infoPromociones}
                  onCambiarCantidad={onCambiarCantidad}
                  onEliminarProducto={onEliminarProducto}
                  onEliminarGuardado={intentarEliminarProductoGuardado}
                  onAbrirModalInstrucciones={abrirModalInstrucciones}
                />
              </div>
            </div>

            {/* Sección para productos guardados */}
            <div className="seccion-guardados">
              <div
                className="seccion-titulo clickable"
                onClick={() => setMostrarGuardados(!mostrarGuardados)}
              >
                <h3>
                  Productos Guardados <span className="contador">{productosGuardados.length}</span>
                  <span className={`toggle-icon ${mostrarGuardados ? 'open' : 'closed'}`}>
                    {mostrarGuardados ? '▼' : '►'}
                  </span>
                </h3>
              </div>

              {mostrarGuardados && (
                <div className="pedido-lista guardados-lista">
                  <ListaProductos
                    productos={productosGuardados}
                    esGuardados={true}
                    productosComplementarios={productosNuevos}
                    infoPromociones={infoPromociones}
                    onCambiarCantidad={onCambiarCantidad}
                    onEliminarProducto={onEliminarProducto}
                    onEliminarGuardado={intentarEliminarProductoGuardado}
                    onAbrirModalInstrucciones={abrirModalInstrucciones}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          // Vista de resumen consolidado
          <div className="vista-resumen">
            <div className="resumen-contenido">
              <table className="resumen-tabla">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Cant. Total</th>
                    <th>Precio</th>
                    <th>Subtotal</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {resumenConsolidado.map((item, index) => {
                    // Si es producto gratis en promoción tipo 3 o tipo 2, mostrar precio 0
                    const esProductoGratis = (item.esPromocionTipo3 || item.esPromocionTipo2) && item.esDescuento;
                    const precioMostrar = esProductoGratis ? 0 : item.precio;
                    const subtotalMostrar = esProductoGratis ? 0 : item.subtotal;

                    // Para productos en promo tipo 2 o 3, mostrar etiqueta especial
                    let estadoEspecial = '';
                    if (item.esPromocionTipo3) {
                      estadoEspecial = item.esDescuento ? ' - Gratis' : ' - En combo';
                    } else if (item.esPromocionTipo2) {
                      estadoEspecial = item.esDescuento ? ' - Con descuento' : ' - En promo 2x1';
                    }

                    return (
                      <tr key={item.idPedidoDetalle || `${item.nombre}-${index}`}
                        className={item.tieneMixto ? 'mixto' : (item.esGuardado ? 'guardado' : 'nuevo')}>
                        <td>{item.nombre}</td>
                        <td>{item.cantidad}</td>
                        <td>${precioMostrar.toFixed(2)}</td>
                        <td>${subtotalMostrar.toFixed(2)}</td>
                        <td>
                          {item.tieneMixto ? (
                            <span className="estado-mixto">Mixto</span>
                          ) : item.esGuardado ? (
                            <span className="estado-guardado">Guardado</span>
                          ) : (
                            <span className="estado-nuevo">Nuevo</span>
                          )}
                          {estadoEspecial && <span className="estado-promocion">{estadoEspecial}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="total-label">Total Productos:</td>
                    <td colSpan="2" className="total-valor">
                      {resumenConsolidado.reduce((sum, item) => sum + item.cantidad, 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Totales del pedido */}
      {pedidoActual.length > 0 && (
        <div className="pedido-totales">
          <div className="total-item subtotal">
            <span>Subtotal:</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>

          {calculandoPromociones && (
            <div className="calculando-promociones">
              <span className="calculando-badge">Calculando promociones...</span>
            </div>
          )}

          {promocionesParaMostrar.length > 0 && (
            <PromocionesResumen
              promocionesAplicadas={promocionesParaMostrar}
              totalDescuentos={totalDescuentos}
            />
          )}

          <div className="total-item total-final">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Modal de autorización para eliminar producto guardado */}
      {modalAutorizacion}

      {/* Modal de instrucciones especiales */}
      {modalInstrucciones}

      {/* Modal para mover productos entre mesas */}
      <MoverProductosModal
        isOpen={mostrarModalMover}
        onClose={() => setMostrarModalMover(false)}
        onMoverProductos={handleMoverProductos}
        productosDisponibles={productosGuardados}
        mesaActual={mesaActual}
        tipoPedido="mesa"
      />
    </div>
  );
};

export default DetallePedidoMejorado;